<script>
  import { onMount } from "svelte";
  import Header from "../components/Header.svelte";
  let blogs = [];
  let filteredBlogs = [];
  let loading = true;
  let searchQuery = "";

  onMount(async () => {
    const res = await fetch("/blogs.json");
    blogs = await res.json();
    filteredBlogs = blogs;
    loading = false;
  });

  function openBlogUrl(url) {
    window.open(url, "_blank", "noopener");
  }

  function searchBlogs() {
    if (searchQuery.trim() === "") {
      filteredBlogs = blogs;
    } else {
      filteredBlogs = blogs.filter(
        (blog) =>
          blog.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          blog.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
  }

  $: if (searchQuery !== undefined) searchBlogs();
</script>

<!-- Use the same background as the main site -->
<div
  class="fixed inset-0 bg-white dark:bg-[#0A0F1C] transition-colors duration-300"
>
  <!-- Enhanced Animated Gradients -->
  <div class="absolute inset-0">
    <div
      class="absolute top-[-20%] -left-[10%] w-[800px] h-[800px]
      bg-gradient-to-r from-blue-500/10 via-violet-500/10 to-purple-500/10 dark:from-blue-500/20 dark:via-violet-500/20 dark:to-purple-500/20 rounded-full
      mix-blend-normal blur-[150px] animate-float transform-gpu"
    ></div>
    <div
      class="absolute bottom-[-20%] -right-[10%] w-[800px] h-[800px]
      bg-gradient-to-r from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 dark:from-purple-500/20 dark:via-fuchsia-500/20 dark:to-pink-500/20 rounded-full
      mix-blend-normal blur-[150px] animate-float-delayed transform-gpu"
    ></div>
  </div>

  <!-- Enhanced Grid Overlay -->
  <div class="absolute inset-0">
    <div
      class="absolute inset-0 bg-[linear-gradient(to_right,#00000005_1px,transparent_1px),linear-gradient(to_bottom,#00000005_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:44px_44px]"
    ></div>
    <div
      class="absolute inset-0 bg-[linear-gradient(to_right,#00000002_1px,transparent_1px),linear-gradient(to_bottom,#00000002_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,#ffffff02_1px,transparent_1px),linear-gradient(to_bottom,#ffffff02_1px,transparent_1px)] bg-[size:15px_15px]"
    ></div>
  </div>
</div>

<main class="relative min-h-screen">
  <section id="blog" class="pt-10 md:pt-20 pb-16">
    <div class="container mx-auto px-4">
      <Header title="All Blogs" subtitle="Read all my latest articles" />

      <div class="text-center mb-12">
        <!-- Search Bar -->
        <div class="max-w-md mx-auto mb-8">
          <div class="relative">
            <div
              class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
            >
              <svg
                class="w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              type="text"
              bind:value={searchQuery}
              placeholder="Search blogs by title or description..."
              class="block w-full pl-10 pr-3 py-3 border border-white/30 dark:border-gray-700/30 rounded-2xl bg-white/30 dark:bg-gray-800/20 backdrop-blur-xl text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
            />
            {#if searchQuery}
              <button
                on:click={() => (searchQuery = "")}
                class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <svg
                  class="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            {/if}
          </div>
          {#if filteredBlogs.length !== blogs.length && searchQuery}
            <p
              class="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center"
            >
              Found {filteredBlogs.length} of {blogs.length} blogs
            </p>
          {/if}
        </div>

        {#if loading}
          <div class="text-xl animate-pulse text-center">Loading...</div>
        {:else if filteredBlogs.length === 0}
          <div class="text-center py-12">
            <svg
              class="w-16 h-16 text-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              stroke-width="1"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <h3
              class="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2"
            >
              No blogs found
            </h3>
            <p class="text-gray-500 dark:text-gray-400">
              Try adjusting your search terms
            </p>
          </div>
        {:else}
          <div
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center"
          >
            {#each filteredBlogs as blog}
              <div
                class="bg-white/30 dark:bg-gray-800/20 backdrop-blur-xl border border-white/30 dark:border-gray-700/30 rounded-3xl p-6 md:p-8 w-full max-w-sm shadow-lg hover:scale-[1.02] hover:shadow-[0_20px_40px_-15px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)] hover:border-blue-100/50 dark:hover:border-blue-500/30 transition-all duration-500 group cursor-pointer"
                on:click={() => openBlogUrl(blog.blogUrl)}
                tabindex="0"
                on:keydown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    openBlogUrl(blog.blogUrl);
                }}
                role="button"
                aria-label={blog.title}
              >
                <img
                  src={blog.blogImageUrl}
                  alt={blog.title}
                  class="w-full h-48 object-cover rounded-2xl mb-4 border-2 border-white/50 dark:border-gray-700/50 shadow"
                />
                <div class="flex items-center gap-3 mb-3">
                  <img
                    src={blog.publisherImageUrl}
                    alt="Publisher"
                    class="w-8 h-8 rounded-full border-2 border-white/50 dark:border-gray-700/50"
                  />
                  <span class="text-xs text-gray-500 dark:text-gray-400"
                    >{blog.date}</span
                  >
                </div>
                <div
                  class="text-lg font-semibold text-blue-600 dark:text-blue-400 group-hover:underline mb-2"
                >
                  {blog.title}
                </div>
                <div class="text-gray-700 dark:text-gray-200 mb-2 line-clamp-3">
                  {blog.description}
                </div>
                <div
                  class="mt-4 text-blue-500 dark:text-blue-400 font-medium group-hover:underline flex items-center gap-1"
                >
                  Read Blog <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                    ><path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    /></svg
                  >
                </div>
              </div>
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </section>
</main>

<style>
  @keyframes float {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(-20px, -20px);
    }
  }

  @keyframes float-delayed {
    0%,
    100% {
      transform: translate(0, 0);
    }
    50% {
      transform: translate(20px, 20px);
    }
  }

  .animate-float {
    animation: float 20s ease-in-out infinite;
  }

  .animate-float-delayed {
    animation: float-delayed 20s ease-in-out infinite;
    animation-delay: -10s;
  }

  .line-clamp-3 {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-clamp: 3;
  }
</style>
